import { cn } from '@/utils/tailwind-util';
import { observer } from 'mobx-react';
import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';
import { UseMutateFunction, useQueryClient } from 'react-query';
import { useParams } from 'react-router-dom';

import SvgIcon from '@/assets/svgIcon';
import Tooltip from '@/components/Tooltip';
import useToast from '@/hooks/useToast';
import { useUploadFiles } from '@/services';
import { UseEditMessageParams, usePostMessage } from '@/services/chatService';
import { chatKey } from '@/services/queryKey';
import { filesToPayload } from '@/utils/fileUpload';
import { handleDownload } from '@/utils/handleDownload';

import { TempMessage, UserType } from '../constants';
import { FileDto } from '../dto';
import { MessageModel } from '../model';
import { useChatStore } from '../store';
import { timeConverter } from '../utils';
import { ProfileAvatar } from './ProfileAvatar';

interface Props {
  message: MessageModel;
  myInfo: any;
  disabled: boolean;
  editMessage: UseMutateFunction<
    any,
    V2ErrorType,
    UseEditMessageParams,
    unknown
  >;
  setImages: Dispatch<SetStateAction<FileDto[]>>;
  setTargetImageIdx: Dispatch<SetStateAction<number>>;
}

enum MODE {
  EDIT_MODE = 'EDIT_MODE',
  NONEDIT_MODE = 'NONEDIT_MODE',
}

export const Message = observer(
  ({
    message,
    myInfo,
    editMessage,
    setImages,
    setTargetImageIdx,
    disabled,
  }: Props) => {
    const [mode, setMode] = useState<MODE>(MODE.NONEDIT_MODE);
    const [value, setValue] = useState(message.content);
    const { venueId } = useParams();
    const $textarea = useRef<HTMLTextAreaElement>(null);
    const store = useChatStore();
    const toast = useToast();
    const queryClient = useQueryClient();
    const computedVenueId = venueId
      ? Number(venueId)
      : store.currentGroup.venueId;

    const handleInputChange = (e) => {
      if (e.target.value.length > 500) {
        e.preventDefault();
        toast.warning('글자는 한 번에 최대 500자까지 입력 가능합니다.', {
          showIcon: true,
        });
      } else {
        setValue(e.target.value);
      }
    };

    const handleFileClick = (file: FileDto) => () => {
      handleDownload([
        {
          url: file.path,
          filename: file.name,
        },
      ]);
    };

    const handleImageClick = (index: number) => () => {
      setImages(message.files.filter((file) => file.type.includes('image')));
      setTargetImageIdx(index);
    };

    const handleEditStart = () => {
      setMode(MODE.EDIT_MODE);
    };

    const handleConfirmEdit = () => {
      setMode(MODE.NONEDIT_MODE);
      editMessage({
        venueId: computedVenueId,
        messageId: message.id,
        groupId: message.groupId,
        content: value,
        fileIds: message.files.map((file) => file.id),
      });
    };

    const handleCancelEdit = () => {
      setMode(MODE.NONEDIT_MODE);
      setValue(message.content);
    };

    const handleError = () => {
      message.failed = true;
      store.addFailCount();
      if (store.failCount > 0) {
        toast.warning(
          '메시지 전송에 실패했습니다. 잠시 후 다시 시도해주세요.',
          {
            showIcon: true,
          }
        );
      }
    };

    const { mutate: mutateMessage, isLoading: isMessageLoading } =
      usePostMessage(
        computedVenueId,
        async function onSuccess(res: TempMessage) {
          store.removeTempMessage(res.tempMessageId);
          store.setMessageMapFromData([res]);
          store.initFailCount();
          if (store.newChatStarted) {
            await queryClient.invalidateQueries(
              chatKey.fetchGroups(computedVenueId)
            );
            store.setDefaultSelectedGroup(res.groupId);
            store.newChatStarted = false;
          }
        },
        function onError() {
          handleError();
        }
      );

    const { mutateAsync: mutateAsyncFile, isLoading: isFileLoading } =
      useUploadFiles(function onError() {
        handleError();
      });

    const handleCancel = () => {
      store.removeTempMessage(message.id);
    };

    const hasFile = message.files.length > 0;

    const isFileUploadDone = () => {
      if (message.fileIds.length > 0) {
        return true;
      }
      return false;
    };

    const getFileIds = async () => {
      if (hasFile) {
        if (isFileUploadDone()) {
          return message.fileIds;
        } else {
          const res = await mutateAsyncFile(
            filesToPayload(message.files as unknown as File[])
          );
          message.fileIds = res.map((fileDto) => fileDto.id);
          return message.fileIds;
        }
      }
      return [];
    };

    const handleRetry = async () => {
      mutateMessage({
        tempMessageId: message.id,
        groupId: store.getGroupId(),
        content: message.content,
        fileIds: await getFileIds(),
      });
    };

    useEffect(() => {
      if (mode === MODE.EDIT_MODE && $textarea.current) {
        $textarea.current.style.height = 'auto';
        $textarea.current.style.height = $textarea.current.scrollHeight + 'px';
      }
    }, [value.length]);

    const LI_STYLES = {
      [MODE.EDIT_MODE]: 'flex w-full gap-3 px-8 py-5 bg-[#f9fafc] relative',
      [MODE.NONEDIT_MODE]: [
        'flex w-full gap-3 px-8 py-5 relative',
        message.from.id === myInfo?.id && !disabled && 'hover:bg-[#f9fafc]',
      ],
    };

    return (
      <li className={cn('group', LI_STYLES[mode])}>
        {message.from.type === UserType.MANAGER ? (
          <SvgIcon width={40} height={40} name="cleanierLogo" />
        ) : (
          <ProfileAvatar src={message.from.thumbnail} />
        )}
        <div className="flex flex-1 flex-col gap-2">
          <div className="flex gap-1 text-sm text-[#8b8b8b]">
            <p className="font-semibold">{message.displayName}</p>
            <p>{timeConverter(message.createdAt)}</p>
            {message.modified && (
              <p className="text-gray-400 text-sm font-medium">(편집됨)</p>
            )}
          </div>
          {mode === MODE.EDIT_MODE && (
            <div className="bg-white">
              <textarea
                ref={$textarea}
                value={value}
                rows={1}
                // eslint-disable-next-line jsx-a11y/no-autofocus
                autoFocus={true}
                style={{ resize: 'none' }}
                className="flex p-5 w-full max-h-96 overflow-y-auto text-base font-medium text-[#171717] focus:outline-none"
                onChange={handleInputChange}
              />
              <div className="flex items-center justify-end bg-white pb-2 mt-2 gap-2 pr-5">
                <button
                  onClick={handleCancelEdit}
                  className="w-16 h-10 border-solid border border-[#98a7f6] rounded font-semibold text-base text-[#4f64d7]"
                >
                  취소
                </button>
                <button
                  disabled={value.trim() === ''}
                  onClick={handleConfirmEdit}
                  className="w-16 h-10 rounded font-semibold text-base bg-[#4f74d7] text-white disabled:bg-[#ececec] disabled:cursor-not-allowed"
                >
                  편집
                </button>
              </div>
            </div>
          )}{' '}
          {mode === MODE.NONEDIT_MODE && (
            <>
              <pre
                style={{ fontFamily: 'Pretendard' }}
                className="relative whitespace-pre-wrap break-all pr-8 text-base font-medium text-[#171717]"
              >
                {value}
              </pre>
              {message.from.id === myInfo?.id && !disabled && (
                <button
                  onClick={handleEditStart}
                  className="group-hover:visible invisible absolute top-1/2 -translate-y-1/2 right-8 max-md:visible"
                >
                  <Tooltip
                    size="md"
                    styles={
                      'bg-[#383940] w-14 flex h-7 rounded items-center justify-center'
                    }
                    label={<SvgIcon width={32} height={32} name="editIcon" />}
                  >
                    <p className="text-white font-normal text-xs">편집하기</p>
                  </Tooltip>
                </button>
              )}
            </>
          )}
          {message.files.length > 0 && (
            <div>
              {message.files.filter((file) => file.type.includes('image'))
                .length > 0 && (
                <div className="flex flex-wrap gap-2 mt-1">
                  {message.files
                    .filter((file) => file.type.includes('image'))
                    .map((file, index) => {
                      const handleImgError = (e) => {
                        // 실제 썸네일을 만들기 까지 시간이 걸리므로, 실패 시 재요청을 한다
                        e.target.src = file.thumbnailPath;
                      };
                      return (
                        <button onClick={handleImageClick(index)} key={file.id}>
                          <img
                            className="h-[160px] min-w-[160px] w-[160px] object-cover"
                            src={file.thumbnailPath}
                            alt="메시지 이미지"
                            onError={handleImgError}
                          />
                        </button>
                      );
                    })}
                </div>
              )}
              {message.files.filter((file) => !file.type.includes('image'))
                .length > 0 && (
                <div className="flex flex-wrap gap-2 mt-1">
                  {message.files
                    .filter((file) => !file.type.includes('image'))
                    .map((file) => (
                      <button
                        key={file.id}
                        className="flex h-[60px] w-[192px] gap-2 rounded border-[1px] border-solid border-[#ececec] p-3"
                        onClick={handleFileClick(file)}
                      >
                        <SvgIcon width={36} height={36} name="pdfIcon" />
                        <div className="flex flex-col items-start">
                          <p className="max-w-[140px] truncate text-xs font-semibold text-black">
                            {file.name}
                          </p>
                          <p className="text-xs font-normal text-[#3d3d3d]">
                            {file.extension}
                          </p>
                        </div>
                      </button>
                    ))}
                </div>
              )}
            </div>
          )}
          {message.failed && (
            <div className="absolute bottom-6 right-8 flex gap-1">
              <Tooltip
                size="md"
                className="-ml-2 flex h-7 items-center bg-[#383940] px-2 text-xs text-white"
                label={
                  <button onClick={handleRetry}>
                    <svg
                      width="32"
                      height="32"
                      viewBox="0 0 32 32"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <rect width="32" height="32" rx="4" fill="#FF2E2D" />
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M21.3127 17.4233C20.5265 20.3574 17.5107 22.0986 14.5766 21.3124C13.6076 21.0528 12.7705 20.5511 12.1108 19.8902L11.7993 19.5786L14.2325 19.5782C14.6467 19.5781 14.9825 19.2423 14.9824 18.8281C14.9823 18.4139 14.6465 18.0781 14.2322 18.0782L9.98881 18.079C9.7899 18.079 9.59914 18.1581 9.45852 18.2987C9.31789 18.4394 9.23891 18.6302 9.23895 18.8291L9.23984 23.071C9.23993 23.4852 9.57579 23.8209 9.99 23.8208C10.4042 23.8207 10.7399 23.4849 10.7398 23.0706L10.7393 20.64L11.0492 20.9499C11.8902 21.7924 12.9579 22.4316 14.1884 22.7613C17.9226 23.7619 21.761 21.5458 22.7616 17.8116C22.8688 17.4115 22.6314 17.0002 22.2313 16.893C21.8312 16.7858 21.4199 17.0232 21.3127 17.4233ZM22.5416 13.7009C22.6823 13.5603 22.7613 13.3695 22.7613 13.1705L22.7606 8.92859C22.7605 8.51438 22.4246 8.17864 22.0104 8.17871C21.5962 8.17878 21.2605 8.51462 21.2606 8.92883L21.261 11.3597L20.9511 11.0499C20.1101 10.2075 19.0422 9.56802 17.8118 9.23835C14.0776 8.23775 10.2392 10.4538 9.23863 14.1881C9.13143 14.5882 9.36886 14.9994 9.76896 15.1067C10.1691 15.2139 10.5803 14.9764 10.6875 14.5763C11.4737 11.6423 14.4896 9.90105 17.4236 10.6872C18.3927 10.9469 19.2297 11.4486 19.8895 12.1095L20.2006 12.4207L17.7686 12.4207C17.3544 12.4207 17.0186 12.7564 17.0186 13.1707C17.0186 13.5849 17.3544 13.9207 17.7686 13.9207H22.0113C22.2102 13.9207 22.401 13.8416 22.5416 13.7009Z"
                        fill="white"
                      />
                    </svg>
                  </button>
                }
              >
                <p>재전송</p>
              </Tooltip>
              <Tooltip
                size="md"
                className="-ml-2 flex h-7 items-center bg-[#383940] px-2 text-xs text-white"
                label={
                  <button onClick={handleCancel}>
                    <svg
                      width="32"
                      height="32"
                      viewBox="0 0 32 32"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <rect width="32" height="32" rx="4" fill="#FFEAEA" />
                      <path
                        d="M12.2803 11.2197C11.9874 10.9268 11.5126 10.9268 11.2197 11.2197C10.9268 11.5126 10.9268 11.9874 11.2197 12.2803L14.9393 16L11.2197 19.7197C10.9268 20.0126 10.9268 20.4874 11.2197 20.7803C11.5126 21.0732 11.9874 21.0732 12.2803 20.7803L16 17.0607L19.7197 20.7803C20.0126 21.0732 20.4874 21.0732 20.7803 20.7803C21.0732 20.4874 21.0732 20.0126 20.7803 19.7197L17.0607 16L20.7803 12.2803C21.0732 11.9874 21.0732 11.5126 20.7803 11.2197C20.4874 10.9268 20.0126 10.9268 19.7197 11.2197L16 14.9393L12.2803 11.2197Z"
                        fill="#757883"
                      />
                    </svg>
                  </button>
                }
              >
                <p>삭제</p>
              </Tooltip>
            </div>
          )}
        </div>
      </li>
    );
  }
);
